import { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto';
import { FileTypeIcon } from '@/components/media/file-type-icon';
import {
  mapMediaItemStatusToneToIconColor,
  resolveMediaAssetStatus,
  resolveMediaStatusLabelKey,
} from '@/helpers/media-item-aggregate-status.helper';
import { buildProcessingInsightTooltip } from '@/helpers/media-processing-insight-tooltip.helper';
import { useTranslations } from '@/i18n/use-translations';
import { MediaItemProcessingSpinner } from './media-item-processing-spinner';

type MediaAssetStatusFileIconProps = {
  asset: MediaAssetDto;
};

/** File-type icon colored by that asset version's status, with a status tooltip. */
export function MediaAssetStatusFileIcon({ asset }: MediaAssetStatusFileIconProps) {
  const t = useTranslations();
  const statusResult = useMemo(() => resolveMediaAssetStatus(asset), [asset]);
  // Pending approval is actioned via the approve switch — keep this icon neutral/active, not primary.
  const iconColor =
    statusResult.status === 'pending_approval' || statusResult.status === 'pending_approval_warning'
      ? 'inherit'
      : mapMediaItemStatusToneToIconColor(statusResult.tone);

  const tooltip = useMemo(() => {
    const lines = [t(resolveMediaStatusLabelKey(statusResult))];
    if (statusResult.insight && statusResult.insight.readiness !== 'not_applicable') {
      const processingTooltip = buildProcessingInsightTooltip(statusResult.insight, t);
      for (const line of processingTooltip.split('\n')) {
        if (!lines.includes(line)) {
          lines.push(line);
        }
      }
    }
    return lines.join('\n');
  }, [statusResult, t]);

  if (!asset.fileType) {
    return null;
  }

  return (
    <Tooltip
      title={tooltip.split('\n').map((line, index) => (
        <Box key={`${index}-${line}`} component="span" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
          {line}
        </Box>
      ))}
      placement="top-start"
      leaveDelay={0}
      disableFocusListener
      disableInteractive
    >
      <Box
        component="span"
        role="img"
        aria-label={tooltip.split('\n')[0]}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
        }}
      >
        {statusResult.showSpinner ? (
          <MediaItemProcessingSpinner status={statusResult.insight?.status} />
        ) : (
          <FileTypeIcon fileType={asset.fileType} color={iconColor} sx={{ fontSize: '1.5rem' }} />
        )}
      </Box>
    </Tooltip>
  );
}
